'use client';

import { useLocale, useTranslations } from 'next-intl';
import { type ChangeEvent, type FC, useRef } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineSubmit2 } from '@/app/components/icons/BaseLineSubmit2';
import { Loading } from '@/app/components/icons/Loading';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Switch } from '@/app/components/ui/switch';
import { TASK_SETTING_OPTION } from '@/app/constants';
import type { ConfigItemOption, FormSubtitlePrompt, OneClickMvSubtitleLanguage, OneClickMvVersion } from '@/app/types';
import { cn } from '@/app/utils';
import { ToolSettingsWithConfig } from '../../_components/ToolSettingsWithConfig';

export type OneClickMvAspectRatio = '1:1' | '16:9' | '9:16' | '4:3' | '3:4';
export type OneClickMvResolution = '720p' | '1080p';

interface OneClickMvSettingsProps {
  aspectRatio: OneClickMvAspectRatio;
  resolution: OneClickMvResolution;
  version: OneClickMvVersion;
  subtitleEnabled: boolean;
  subtitleLanguage?: OneClickMvSubtitleLanguage;
  subtitlePrompts: FormSubtitlePrompt[];
  onAspectRatioChange: (value: OneClickMvAspectRatio) => void;
  onResolutionChange: (value: OneClickMvResolution) => void;
  onSubtitleFile: (file: File) => void;
  onRemoveSubtitle: (id: string) => void;
  onSubtitleEnabledChange: (enabled: boolean) => void;
  onSubtitleLanguageChange: (language: OneClickMvSubtitleLanguage) => void;
}

const ASPECT_RATIO_OPTIONS: ConfigItemOption[] = ['1:1', '16:9', '9:16', '4:3', '3:4'].map((value) => ({
  value,
  label: value,
  showVip: false,
}));

const RESOLUTION_OPTIONS: ConfigItemOption[] = ['720p', '1080p'].map((value) => ({
  value,
  label: value.toUpperCase(),
  showVip: false,
}));

const INCLUDED_OPTIONS: TASK_SETTING_OPTION[] = [
  TASK_SETTING_OPTION.AspectRatio,
  TASK_SETTING_OPTION.Resolution,
  TASK_SETTING_OPTION.Subtitle,
];

const SUBTITLE_LANGUAGE_OPTIONS: OneClickMvSubtitleLanguage[] = [
  'auto',
  'en',
  'zh',
  'ja',
  'ko',
  'vi',
  'th',
  'id',
  'ms',
  'tl',
  'hi',
  'ar',
  'fr',
  'de',
  'es',
  'pt',
  'ru',
  'it',
  'nl',
  'sv',
  'da',
  'fi',
  'no',
  'el',
  'pl',
  'cs',
  'hu',
  'ro',
  'bg',
  'hr',
  'sk',
];

const getSubtitleFileName = (item: FormSubtitlePrompt): string => {
  if (item.prompt?.fileName) return item.prompt.fileName;
  if (item.prompt?._tempFile?.name) return item.prompt._tempFile.name;
  return '';
};

export const OneClickMvSettings: FC<OneClickMvSettingsProps> = ({
  aspectRatio,
  resolution,
  version,
  subtitleEnabled,
  subtitleLanguage,
  subtitlePrompts,
  onAspectRatioChange,
  onResolutionChange,
  onSubtitleFile,
  onRemoveSubtitle,
  onSubtitleEnabledChange,
  onSubtitleLanguageChange,
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const subtitleFileInputRef = useRef<HTMLInputElement>(null);
  const languageNames = useRef<Intl.DisplayNames | null>(null);

  if (!languageNames.current) {
    try {
      languageNames.current = new Intl.DisplayNames([locale], { type: 'language' });
    } catch {
      languageNames.current = null;
    }
  }

  const handleSubtitleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      onSubtitleFile(file);
    }
  };

  const openFilePicker = () => {
    subtitleFileInputRef.current?.click();
  };

  const subtitleUploadOption = (
    <div className="space-y-3">
      <div className="font-normal text-sm text-system-white leading-[1.4]">{t('tools.subtitleSettings.label')}</div>
      <input ref={subtitleFileInputRef} hidden type="file" accept=".srt" onChange={handleSubtitleFileInputChange} />
      <div className="space-y-2">
        {subtitlePrompts.map((item) => (
          <div
            key={item.id}
            className={cn(
              'flex h-12 w-full items-center justify-between gap-2 rounded-12 border border-system-white08 bg-system-white04 px-3 backdrop-blur-[30px]',
            )}
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <BaseLineSubmit2 className="size-4 shrink-0 text-system-white/60" />
              <span
                className={cn(
                  'truncate text-xs',
                  item.status === 'uploading' ? 'text-system-white/40' : 'text-system-white/60',
                )}
              >
                {item.status === 'uploading' ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loading className="size-3.5 shrink-0" />
                    {t('tools.uploadNewFile')}
                  </span>
                ) : (
                  getSubtitleFileName(item)
                )}
              </span>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-6 p-1 text-system-white/60 transition-colors hover:bg-system-white08 hover:text-system-white"
              onClick={() => onRemoveSubtitle(item.id)}
            >
              <BaseLineClose className="size-4" />
            </button>
          </div>
        ))}
        {subtitlePrompts.length === 0 && (
          <button
            type="button"
            className={cn(
              'flex h-12 w-full items-center justify-center gap-2 rounded-12 border border-system-white08 border-dashed bg-transparent text-system-white/40 text-xs transition-colors hover:bg-system-white04 hover:text-system-white/60',
            )}
            onClick={openFilePicker}
          >
            <BaseLineSubmit2 className="size-4 shrink-0" />
            <span>{t('tools.uploadNewFile')}</span>
          </button>
        )}
      </div>
    </div>
  );
  const subtitleSwitchOption = (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm text-system-white">{t('tools.oneClickMv.subtitle.autoLabel')}</div>
          <div className="mt-1 text-system-white/40 text-xs leading-[1.4]">
            {t('tools.oneClickMv.subtitle.autoHelper')}
          </div>
        </div>
        <Switch className="h-7 w-12 p-0.5" checked={subtitleEnabled} onCheckedChange={onSubtitleEnabledChange} />
      </div>
      {subtitleEnabled && (
        <div className="space-y-2">
          <div className="text-sm text-system-white">{t('tools.oneClickMv.subtitle.languageLabel')}</div>
          <Select
            value={subtitleLanguage ?? 'auto'}
            onValueChange={(value) => onSubtitleLanguageChange(value as OneClickMvSubtitleLanguage)}
          >
            <SelectTrigger className="h-10 w-full rounded-8 border border-system-white08 bg-system-white04 px-3 text-sm text-system-white shadow-none focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUBTITLE_LANGUAGE_OPTIONS.map((language) => (
                <SelectItem key={language} value={language}>
                  {language === 'auto'
                    ? t('tools.oneClickMv.subtitle.autoLanguage')
                    : (languageNames.current?.of(language) ?? language)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );

  return (
    <ToolSettingsWithConfig
      className="w-full"
      includedOptions={INCLUDED_OPTIONS}
      showOptionDivider={false}
      optionsWrapperClassName="gap-5 md:gap-5"
      optionsClassName={['[&>div]:!gap-3', '[&>div]:!gap-3']}
      aspectRatioOption={{
        value: aspectRatio,
        options: ASPECT_RATIO_OPTIONS,
        onChange: (value) => onAspectRatioChange(value as OneClickMvAspectRatio),
      }}
      resolutionOption={{
        value: resolution,
        options: RESOLUTION_OPTIONS,
        onChange: (value) => onResolutionChange(value as OneClickMvResolution),
      }}
      subtitleOption={version === 'v1' ? subtitleUploadOption : subtitleSwitchOption}
    />
  );
};
